/* eslint-disable max-lines */
import 'react-pdf/dist/esm/Page/TextLayer.css';
import './TextLayerFix.css';
import 'reactApp/modules/pdfEditor/initPdfWorker';

import { usePrevious } from '@vkontakte/vkui/dist/hooks/usePrevious';
import classNames from 'clsx';
import debounce from 'lodash.debounce';
import React, {
    forwardRef,
    ForwardRefRenderFunction,
    ReactElement,
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { BUILD_URLS } from 'reactApp/appHelpers/configHelpers';
import { noop } from 'reactApp/utils/helpers';
import useResizeObserver from 'use-resize-observer/polyfilled';

import styles from './PdfViewer.css';

interface BufferPageProps {
    num: number;
    height: number;
    width: number;
    margin: number;
    handleRef?: React.LegacyRef<HTMLDivElement>;
}

const BufferPage = ({ num, height, width, margin, handleRef }: BufferPageProps) => (
    <div className={styles.pageWrapper} ref={handleRef} data-page-number={num}>
        <div className={styles.buffer} style={{ height: margin / 2 }} />
        <div className={classNames(styles.page)}>
            <div
                style={{
                    height,
                    width,
                }}
            />
        </div>
        <div className={styles.buffer} style={{ height: margin / 2 }} />
    </div>
);

export type PdfViewerProps = {
    content: ArrayBuffer;
    pageClassName?: string;
    selectedClassName?: string;
    saveLoaded?: boolean;
    nativeWidth?: boolean;
    mobile?: boolean;
    contentOffsets?: number[];
    bufferPages?: number;
    pagesMargin?: number;
    currentPage?: number;
    selectedPage?: number;
    onError?: (error: string) => void;
    onPageClick?: (event: React.MouseEvent, page: number) => void;
    onPageRender?: (pageNumber: number) => void;
    onCurrentPageChange?: (num: number) => void;
    zoomScale?: number;
    maxWidth?: number;
    password?: string;
    documentScale?: number;
    customTextRenderer?: ({ str }: { str: string }) => ReactElement;
    renderTextLayer?: boolean;
    onLoadSuccessCallback?: () => void;
    scrollWrapperRef?: RefObject<HTMLDivElement>;
};

const DEFAULT_TOP_OFFSET = 10;
const DEFAULT_TOP_OFFSET_MOBILE = 68;
const DEFAULT_BOTTOM_OFFSET = 10;
const DEFAULT_BOTTOM_OFFSET_MOBILE = 50;
const DEFAULT_SIDES_OFFSET = 10;

const PdfViewerComponent: ForwardRefRenderFunction<HTMLDivElement, PdfViewerProps> = (
    {
        pageClassName,
        selectedClassName,
        selectedPage,
        currentPage,
        content,
        onError,
        onPageClick = noop,
        onCurrentPageChange,
        bufferPages = 5,
        pagesMargin = 10,
        contentOffsets,
        mobile = false,
        saveLoaded = false,
        nativeWidth = false,
        zoomScale = 1,
        maxWidth,
        password = '',
        onPageRender,
        documentScale = 1,
        renderTextLayer = false,
        onLoadSuccessCallback = noop,
        scrollWrapperRef,
    },
    ref
) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [pagesRefs, setPagesRefs] = useState<Record<number, HTMLDivElement | undefined>>({});
    const [numPages, setNumPages] = useState(0);
    const [page, setPage] = useState(currentPage || 1);
    const [pagesHeights, setPagesHeights] = useState<Record<number, number>>({});
    const [pagesWidths, setPagesWidths] = useState<Record<number, number>>({});
    const [renderPages, setRenderPages] = useState<Array<number>>([]);
    const [width, setWidth] = useState(0);
    const [calcWidth, setCalcWidth] = useState(false);
    const prevWidth = usePrevious(width);
    const [waitRender, setWaitRender] = useState(false);
    const [calcCurrentPage, setCalcCurrentPage] = useState(false);
    const [calcPageSize, setCalcPageSize] = useState(0);
    const [scrollToPage, setScrollToPage] = useState({ num: 0, arg: true });

    useEffect(() => {
        setNumPages(0);
        setPagesRefs({});
    }, [content]);

    useEffect(() => {
        if (!scrollToPage.num || waitRender) {
            return;
        }
        const scrollPage = pagesRefs[scrollToPage.num];

        if (scrollPage) {
            scrollPage.scrollIntoView(scrollToPage.arg);
            setScrollToPage({ num: 0, arg: true });
        }
    }, [scrollToPage, waitRender, pagesRefs]);

    useEffect(() => {
        if (!prevWidth) {
            setWaitRender(false);
            return;
        }

        const newHeights = {};
        Object.entries(pagesRefs).forEach(([num, ref]) => {
            if (!num || !ref) {
                return;
            }
            const isRealPage = ref.children.item(0)?.classList.contains('react-pdf__Page');
            if (isRealPage) {
                newHeights[num] = ref.getBoundingClientRect().height;
            } else {
                newHeights[num] = (pagesHeights[num] - pagesMargin) * (width / prevWidth) + pagesMargin;
            }
        });

        setPagesHeights(newHeights);

        setTimeout(() => {
            if (scrollWrapperRef?.current) {
                const rect = contentRef.current?.getBoundingClientRect();
                const center = (window.innerHeight || document.documentElement.clientHeight) / 2;
                const isTopHigherCenter = rect && rect.top <= center;
                const isBottomLowerCenter = rect && rect.bottom >= center;

                if (isTopHigherCenter && isBottomLowerCenter) {
                    setScrollToPage({ num: page, arg: true });
                }
            } else {
                setScrollToPage({ num: page, arg: true });
            }

            setWaitRender(false);
        }, 0);
    }, [width]);

    useEffect(() => {
        return () => {
            setPagesRefs({});
        };
    }, []);

    useEffect(() => {
        if (ref && contentRef.current && typeof ref === 'function') {
            ref(contentRef.current);
        } else if (ref && contentRef.current && typeof ref === 'object') {
            ref.current = contentRef.current;
        }
    }, [ref, contentRef.current]);

    useEffect(() => {
        if (!currentPage || page === currentPage) {
            return;
        }
        setWaitRender(true);
        setPage(currentPage);
        setScrollToPage({ num: currentPage, arg: true });
    }, [currentPage]);

    useEffect(() => {
        if (!contentRef.current || !selectedPage || page === selectedPage) {
            return;
        }

        const pageRef = pagesRefs[selectedPage];
        if (pageRef) {
            const { top: pageTop, height: pageHeight } = pageRef.getBoundingClientRect();
            const { top: contentTop, height: contentHeight } = (scrollWrapperRef?.current || contentRef.current).getBoundingClientRect();

            if (pageTop < contentTop) {
                setScrollToPage({ num: selectedPage, arg: true });
            } else if (pageTop + pageHeight > contentTop + contentHeight) {
                setScrollToPage({ num: selectedPage, arg: false });
            }
        } else {
            setWaitRender(true);
            setPage(selectedPage);

            if (page < selectedPage) {
                setScrollToPage({ num: selectedPage, arg: true });
            } else {
                setScrollToPage({ num: selectedPage, arg: false });
            }
        }
    }, [selectedPage, contentRef.current, scrollWrapperRef?.current]);

    useEffect(() => {
        onCurrentPageChange?.(page);
    }, [page, onCurrentPageChange]);

    useEffect(() => {
        if (!page || !bufferPages || !numPages) {
            return;
        }
        const pages: number[] = [];

        const start = Math.max(1, Math.floor((page - bufferPages) / bufferPages) * bufferPages);
        const end = Math.min(numPages, Math.ceil((page + bufferPages) / bufferPages) * bufferPages);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (saveLoaded) {
            setRenderPages((prev) => {
                return prev
                    .concat(pages)
                    .filter((value, index, self) => self.indexOf(value) === index)
                    .sort((a, b) => a - b);
            });
        } else {
            setRenderPages(pages);

            setPagesRefs((pagesRefs) => {
                const newPagesRefs = {};

                pages.forEach((num) => {
                    newPagesRefs[num] = pagesRefs[num];
                });

                return newPagesRefs;
            });
        }
    }, [page, bufferPages, numPages, saveLoaded]);

    useEffect(() => {
        if (!calcCurrentPage) {
            return;
        }

        // wait until pages fully render
        if (waitRender || scrollToPage.num) {
            setCalcCurrentPage(false);
            return;
        }
        const currentPage = pagesRefs[page];
        if (!currentPage || !contentRef.current) {
            setCalcCurrentPage(false);
            return;
        }
        const { top, height: pageHeight } = currentPage.getBoundingClientRect();
        const { top: contentTop, height: contentHeight } = (scrollWrapperRef?.current || contentRef.current).getBoundingClientRect();

        const pageTop = top - 10;
        const pageBottom = pageTop + pageHeight + 10;
        const contentCenter = contentTop + contentHeight / 2;

        if (pageBottom < contentCenter) {
            const pagesToJump = Math.ceil((contentCenter - pageBottom) / pageHeight);
            const newPageNum = Math.min(numPages, page + pagesToJump);
            if (newPageNum !== page) {
                setPage(newPageNum);
                setWaitRender(true);
            }
        }
        if (pageTop > contentCenter) {
            const pagesToJump = Math.ceil((pageTop - contentCenter) / pageHeight);
            const newPageNum = Math.max(1, page - pagesToJump);
            if (newPageNum !== page) {
                setPage(newPageNum);
                setWaitRender(true);
            }
        }

        setCalcCurrentPage(false);
    }, [calcCurrentPage]);

    const pageSideOffset = useMemo(() => {
        return contentOffsets?.[1] || contentOffsets?.[0] || DEFAULT_SIDES_OFFSET;
    }, [contentOffsets]);

    useEffect(() => {
        if (!contentRef.current || zoomScale !== 1) {
            return;
        }

        const { width: wrapperWidth } = contentRef.current.getBoundingClientRect();
        const pageWidth = wrapperWidth - pageSideOffset * 2;

        if (pageWidth !== width) {
            setWidth((width) => {
                if (width === pageWidth) {
                    setWaitRender(false);
                }
                return pageWidth;
            });
        } else {
            setWaitRender(false);
        }

        setCalcWidth(false);
    }, [calcWidth, zoomScale]);

    const handleOnResize = useCallback(
        debounce(() => {
            setWaitRender(true);
            setCalcWidth(true);
        }, 100),
        []
    );

    useResizeObserver<HTMLDivElement>({ ref: contentRef, onResize: handleOnResize });

    useEffect(() => {
        if (pagesRefs[page]) {
            setTimeout(() => {
                setWaitRender(false);
                setCalcCurrentPage(true);
            }, 300);
        }
    }, [pagesRefs, page]);

    useEffect(() => {
        if (!contentRef.current) {
            return;
        }

        const fn = () => setCalcCurrentPage(true);

        (scrollWrapperRef?.current || contentRef.current).addEventListener('scroll', fn);

        return () => {
            (scrollWrapperRef?.current || contentRef.current)?.removeEventListener('scroll', fn);
        };
    }, [contentRef.current, scrollWrapperRef?.current]);

    const file = useMemo(() => ({ data: new Uint8Array(content) }), [content]);

    const options = useMemo(
        () => ({
            standardFontDataUrl: `${BUILD_URLS?.css}fonts/pdf/`,
            cMapUrl: `${BUILD_URLS?.css}fonts/pdf/cmaps/`,
            cMapPacked: true,
        }),
        []
    );

    useEffect(() => {
        const ref = pagesRefs[calcPageSize];
        if (!ref) {
            return;
        }
        const realPage = ref.querySelector('.react-pdf__Page');

        if (realPage) {
            const { height, width } = realPage.getBoundingClientRect();
            setPagesHeights((pagesHeights) => ({ ...pagesHeights, [calcPageSize]: height / zoomScale }));
            setPagesWidths((pagesWidth) => ({ ...pagesWidth, [calcPageSize]: width / zoomScale }));
            onPageRender?.(calcPageSize);
        }
    }, [calcPageSize]);

    const handleRef = useCallback((page: HTMLDivElement | null) => {
        if (!page) {
            return;
        }

        const pageNumber = Number(page.dataset.pageNumber);

        setPagesRefs((pagesRefs) => ({ ...pagesRefs, [pageNumber]: page }));

        setTimeout(() => {
            setCalcPageSize(pageNumber);
        }, 200);
    }, []);

    const onLoadSuccess = useCallback(({ numPages }: pdfjs.PDFDocumentProxy) => {
        setCalcWidth(true);
        setNumPages(numPages);
        setPage((value) => value || 1);

        if (onLoadSuccessCallback) {
            onLoadSuccessCallback();
        }
    }, []);

    const onLoadError = useCallback((error: Error) => {
        onError?.(error?.message || 'Что-то пошло не так');
    }, []);

    const defaultPageHeight = useMemo(() => {
        const values = Array.from(new Array(10).keys())
            .map((num) => pagesHeights[num])
            .filter(Boolean);

        if (!values.length) {
            return 500;
        }

        return Math.min(...values);
    }, [pagesHeights[2], pagesHeights[5], pagesHeights[10]]);

    const defaultPageWidth = useMemo(() => {
        const values = Array.from(new Array(10).keys())
            .map((num) => pagesWidths[num])
            .filter(Boolean);

        if (!values.length) {
            return 500;
        }

        return Math.min(...values);
    }, [pagesWidths[2], pagesWidths[5], pagesWidths[10]]);

    const topBufferPages = useMemo(() => {
        if (!renderPages.length) {
            return [];
        }

        const pages: Array<number> = [];

        const from = Math.max(1, renderPages[0] - 5);
        const until = renderPages[0] || 1;

        for (let i = from; i < until; i++) {
            pages.push(i);
        }

        return pages;
    }, [renderPages]);

    const topBufferOffset = useMemo(() => {
        let offset = 0;

        const from = 1;
        const until = topBufferPages[0] || 1;

        for (let i = from; i < until; i++) {
            offset += pagesHeights[i] || defaultPageHeight;
        }

        return offset;
    }, [topBufferPages, pagesHeights, defaultPageHeight]);

    const topBufferStyles = useMemo(() => {
        const defaultOffset = mobile ? DEFAULT_TOP_OFFSET_MOBILE : DEFAULT_TOP_OFFSET;
        const topOffset = contentOffsets?.[0] || defaultOffset;

        return {
            height: (topOffset - pagesMargin / 2) / zoomScale,
            flexGrow: 1,
        };
    }, [contentOffsets, zoomScale, mobile, pagesMargin]);

    const bottomBufferPages = useMemo(() => {
        if (!renderPages.length) {
            return [];
        }

        const pages: Array<number> = [];

        const lastRenderedPage = renderPages[renderPages.length - 1];
        const from = lastRenderedPage + 1;
        const until = Math.min(numPages, lastRenderedPage + 5);

        for (let i = from; i <= until; i++) {
            pages.push(i);
        }

        return pages;
    }, [numPages, renderPages]);

    const bottomBufferOffset = useMemo(() => {
        let offset = 0;

        const from = bottomBufferPages[bottomBufferPages.length - 1] || numPages;
        const until = numPages;

        for (let i = from; i < until; i++) {
            offset += pagesHeights[i] || defaultPageHeight;
        }

        return offset;
    }, [bottomBufferPages, defaultPageHeight, numPages, pagesHeights]);

    const bottomBufferStyles = useMemo(() => {
        const defaultBottomOffset = mobile ? DEFAULT_BOTTOM_OFFSET_MOBILE : DEFAULT_BOTTOM_OFFSET;
        const bottomOffset = contentOffsets?.[2] || contentOffsets?.[0] || defaultBottomOffset;

        return {
            height: (bottomOffset - pagesMargin / 2) / zoomScale,
            flexGrow: 1,
        };
    }, [mobile, contentOffsets, zoomScale, pagesMargin]);
    const renderBufferPage = useCallback(
        (num: number) => (
            <BufferPage
                key={num}
                num={num}
                height={(pagesHeights[num] || defaultPageHeight) - pagesMargin}
                width={pagesWidths[num] || defaultPageWidth}
                margin={pagesMargin}
                handleRef={handleRef}
            />
        ),
        [handleRef, pagesHeights, defaultPageHeight, pagesMargin, pagesWidths, defaultPageWidth]
    );

    const handlePassword = useCallback(
        (callback: (password: string) => void, reason: number) => {
            if (reason === pdfjs.PasswordResponses.NEED_PASSWORD && password) {
                callback(password);
            } else {
                const password = prompt(reason === pdfjs.PasswordResponses.NEED_PASSWORD ? 'Пароль' : 'Неверный пароль');

                if (password === null) {
                    onError?.(
                        reason === pdfjs.PasswordResponses.NEED_PASSWORD ? 'The document is password protected' : 'Incorrect password'
                    );
                } else {
                    callback(password);
                }
            }
        },
        [password]
    );

    const handlePageClick = useCallback((event: React.MouseEvent) => {
        const pageNumber = Number((event.currentTarget as any)?.dataset?.pageNumber);
        if (pageNumber) {
            onPageClick(event, pageNumber);
        }
    }, []);

    const renderWidth = useMemo(() => {
        if (nativeWidth) {
            return undefined;
        }
        if (maxWidth) {
            return Math.min(maxWidth, width);
        }

        return width;
    }, [nativeWidth, maxWidth, width]);

    return (
        <div ref={contentRef} className={classNames(styles.root, { [styles.mobile]: mobile })} body-scroll-lock-ignore="true">
            <Document
                file={file}
                options={options}
                onLoadSuccess={onLoadSuccess}
                onLoadError={onLoadError}
                onSourceError={onLoadError}
                loading=""
                onPassword={handlePassword}
            >
                {!!numPages && !!width && (
                    <>
                        <div className={styles.buffer} style={topBufferStyles} />

                        {!!topBufferOffset && (
                            <BufferPage key={1} num={1} height={topBufferOffset} width={defaultPageWidth} margin={pagesMargin} />
                        )}

                        {topBufferPages.map(renderBufferPage)}

                        {renderPages.map((num) => (
                            <div
                                className={classNames(styles.pageWrapper, pageClassName, {
                                    [selectedClassName || '']: selectedPage === num,
                                })}
                                key={num}
                                ref={handleRef}
                                data-page-number={num}
                                onClick={handlePageClick}
                            >
                                <div className={styles.buffer} style={{ height: pagesMargin / 2 }} />
                                <Page
                                    renderMode="canvas"
                                    className={classNames(styles.page)}
                                    renderTextLayer={renderTextLayer}
                                    renderAnnotationLayer={false}
                                    renderInteractiveForms={false}
                                    width={renderWidth}
                                    pageNumber={num}
                                    onRenderError={onLoadError}
                                    loading=""
                                    scale={documentScale}
                                />
                                <div className={styles.buffer} style={{ height: pagesMargin / 2 }} />
                            </div>
                        ))}

                        {bottomBufferPages.map(renderBufferPage)}

                        {!!bottomBufferOffset && (
                            <BufferPage
                                key={numPages}
                                num={numPages}
                                height={bottomBufferOffset}
                                width={defaultPageWidth}
                                margin={pagesMargin}
                            />
                        )}

                        <div className={styles.buffer} style={bottomBufferStyles} />
                    </>
                )}
            </Document>
        </div>
    );
};

export const PdfViewer = forwardRef(PdfViewerComponent);
